import { memo, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ReloadOutlined } from '@ant-design/icons';
import { destructive } from 'utils/designTokens';
import AddIcon from '@mui/icons-material/Add';
import PageHeader from 'components/PageHeader';
import PaymentMethodForm from './PaymentMethodForm';
import MainCard from 'components/MainCard';
import TableActionButton from 'components/table/TableActionButton';
import TableActions from 'components/table/TableActions';
import TableLoadedFooter from 'components/table/TableLoadedFooter';
import TableActionCell from 'components/table/TableActionCell';
import TableStatusChip from 'components/table/TableStatusChip';
import api from 'utils/api';
import { useToast } from 'contexts/ToastContext';

function parsePaymentMethodListResponse(res) {
  const body = res?.data;
  const data = body?.data ?? body;

  const methods =
    data?.payment_methods ??
    data?.paymentMethods ??
    data?.methods ??
    data?.list ??
    (Array.isArray(data) ? data : []);

  return Array.isArray(methods) ? methods : [];
}

function getMethodId(method) {
  return method?.id ?? method?.payment_method_id ?? method?.method_id;
}

function getMethodName(method) {
  return method?.name ?? method?.name_en ?? method?.method_name ?? method?.payment_method_name ?? '—';
}

function getMethodCode(method) {
  return method?.code ?? method?.method_code ?? method?.slug ?? '—';
}

function getMethodType(method) {
  const raw =
    method?.type ??
    method?.payment_type ??
    method?.method_type ??
    method?.PaymentMethod?.type ??
    null;
  if (raw == null || raw === '') return null;
  return String(raw);
}

function isMethodActive(method) {
  if (method?.is_active !== undefined) return Boolean(method.is_active);
  if (method?.status !== undefined) {
    if (typeof method.status === 'boolean') return method.status;
    return String(method.status).toLowerCase() === 'active' || method.status === 1;
  }
  return false;
}

const PaymentMethodRow = memo(function PaymentMethodRow({
  method,
  index,
  togglingId,
  onToggleStatus,
  onEdit,
  onDelete
}) {
  const id = getMethodId(method);
  const active = isMethodActive(method);
  const methodType = getMethodType(method);
  const isToggling = togglingId === id;

  return (
    <TableRow hover>
      <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {getMethodName(method)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {getMethodCode(method)}
        </Typography>
      </TableCell>
      <TableCell>
        {methodType ? (
          <Chip
            label={methodType}
            size="small"
            variant="outlined"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none'
            }}
          />
        ) : (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell align="center">
        <Tooltip title="Click to toggle">
          <span>
            <TableStatusChip
              active={active}
              label={active ? 'Active' : 'Inactive'}
              loading={isToggling}
              loadingLabel={active ? 'Active' : 'Inactive'}
              onClick={() => onToggleStatus(method)}
              sx={{ minWidth: 72 }}
            />
          </span>
        </Tooltip>
      </TableCell>
      <TableActionCell>
        <TableActions>
          <TableActionButton action="edit" title="Edit" onClick={() => onEdit(method)} />
          <TableActionButton
            action="delete"
            title="Delete"
            onClick={() => onDelete(method)}
          />
        </TableActions>
      </TableActionCell>
    </TableRow>
  );
});

export default function PaymentMethodList() {
  const { showApiError } = useToast();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [formModal, setFormModal] = useState({ open: false, id: null, method: null });
  const [delDialog, setDelDialog] = useState({ open: false, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  const openAddForm = () => setFormModal({ open: true, id: null, method: null });
  const openEditForm = (method) =>
    setFormModal({ open: true, id: getMethodId(method), method });
  const closeForm = () => setFormModal({ open: false, id: null, method: null });

  const loadMethods = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/payment/method/list');
      if (res.data?.status === false) {
        throw new Error(res.data?.message || 'Failed to load payment methods');
      }
      setMethods(parsePaymentMethodListResponse(res));
    } catch (err) {
      setMethods([]);
      showApiError(err, 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  }, [showApiError]);

  useEffect(() => {
    loadMethods();
  }, [loadMethods]);

  const openDeleteDialog = (method) =>
    setDelDialog({
      open: true,
      id: getMethodId(method),
      name: getMethodName(method)
    });

  const handleDelete = async () => {
    if (!delDialog.id) return;
    setDeleting(true);
    try {
      await api.delete(`/payment/method/delete/${delDialog.id}`);
      setMethods((prev) => prev.filter((m) => getMethodId(m) !== delDialog.id));
      setDelDialog({ open: false, id: null, name: '' });
    } catch (err) {
      showApiError(err, 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (method) => {
    const id = getMethodId(method);
    if (!id || togglingId) return;

    const previousStatus = isMethodActive(method);
    const nextStatus = !previousStatus;

    setTogglingId(id);
    setMethods((prev) =>
      prev.map((m) =>
        getMethodId(m) === id ? { ...m, status: nextStatus, is_active: nextStatus } : m
      )
    );

    try {
      const res = await api.put(`/payment/method/status/${id}`, { status: nextStatus });
      const confirmed = res.data?.data?.status ?? res.data?.data?.is_active;
      if (confirmed !== undefined) {
        const updatedStatus = Boolean(confirmed);
        setMethods((prev) =>
          prev.map((m) =>
            getMethodId(m) === id ? { ...m, status: updatedStatus, is_active: updatedStatus } : m
          )
        );
      }
    } catch (err) {
      setMethods((prev) =>
        prev.map((m) =>
          getMethodId(m) === id
            ? { ...m, status: previousStatus, is_active: previousStatus }
            : m
        )
      );
      showApiError(err, 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Payment Methods"
        subtitle="View and manage payment method availability."
        count={methods.length}
        actionLabel="Add Payment Method"
        actionIcon={AddIcon}
        onAction={openAddForm}
      />

      <MainCard contentSX={{ p: 0 }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            display: 'block',
            maxHeight: { xs: '55vh', md: 520 },
            overflowX: 'auto',
            overflowY: 'auto',
            borderBottom: 'none',
            '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
              borderBottom: 'none'
            }
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700, width: 48 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 100 }} align="center">
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 80 }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && methods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : methods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No payment methods found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                methods.map((method, idx) => (
                  <PaymentMethodRow
                    key={getMethodId(method) ?? idx}
                    method={method}
                    index={idx}
                    togglingId={togglingId}
                    onToggleStatus={handleToggleStatus}
                    onEdit={openEditForm}
                    onDelete={openDeleteDialog}
                  />
                ))
              )}
              {!loading && methods.length > 0 ? (
                <TableLoadedFooter count={methods.length} singular="payment method" colSpan={6} />
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </MainCard>

      <PaymentMethodForm
        open={formModal.open}
        paymentMethodId={formModal.id}
        initialMethod={formModal.method}
        onSave={() => loadMethods({ silent: true })}
        onClose={closeForm}
      />

      <Dialog
        open={delDialog.open}
        onClose={() => !deleting && setDelDialog({ open: false, id: null, name: '' })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Payment Method</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{delDialog.name}</strong>? This cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1.5 }}>
          <Button
            onClick={() => setDelDialog({ open: false, id: null, name: '' })}
            disabled={deleting}
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            size="small"
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{
              bgcolor: destructive.main,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: destructive.shadow,
              '&:hover': { bgcolor: destructive.dark },
              '&.Mui-disabled': { bgcolor: 'rgba(183, 28, 28, 0.45)', color: '#fff' }
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
